import React, { useState } from "react";
import { VERIFICATION_CODE_LENGTH } from "@constants";
import { OTPField } from "@design-system/OTPField/OTPField";
import type { OTPFieldForcedState } from "@design-system/OTPField/OTPField.types";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import {
    useAnatomyMode,
    type AnatomyForcedState,
} from "./componentAnatomyMode";
import {
    codeFillStates,
    codeFillValues,
    OTPFieldMatrix,
    type CodeFillState,
} from "./OTPFieldPlaygroundMatrix";
import { BooleanControl, TextControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";

const codeFillStateForValue = (value: string): CodeFillState => {
    if (value.length === 0) {
        return "empty";
    }

    return value.length < VERIFICATION_CODE_LENGTH ? "partial" : "complete";
};

const toDigits = (value: string) =>
    value.replace(/\D/g, "").slice(0, VERIFICATION_CODE_LENGTH);

/** OTPField has no pressed state, so that one state drops on the way through. */
const toOTPFieldForcedState = (
    state: AnatomyForcedState | undefined,
): OTPFieldForcedState | undefined => {
    switch (state) {
        case "hover":
        case "focus":
            return state;
        default:
            return undefined;
    }
};

/** Sandbox for OTPField: value presets, invalid and disabled, x-ray and the states matrix. */
export const OTPFieldPlayground = () => {
    const [value, setValue] = useState(codeFillValues.partial);
    const [isInvalid, setIsInvalid] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);

    const fillState = codeFillStateForValue(value);
    const anatomy = useAnatomyMode({
        component: "otpField",
        variantId: "default",
        isDisabled,
        isInvalid,
    });
    const forcedState = toOTPFieldForcedState(anatomy.forcedState);

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Value</h2>
                        <ToggleGroup.Root<CodeFillState>
                            value={fillState}
                            onValueChange={(nextFillState) => {
                                if (nextFillState) {
                                    setValue(codeFillValues[nextFillState]);
                                }
                            }}
                        >
                            {codeFillStates.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                        <h2 className={styles.subheading}>Code</h2>
                        <TextControl
                            label="Digits"
                            value={value}
                            onChange={(nextValue) =>
                                setValue(toDigits(nextValue))
                            }
                        />
                    </section>
                    <section>
                        <h2>Features</h2>
                        <BooleanControl
                            label="Invalid"
                            checked={isInvalid}
                            onChange={setIsInvalid}
                        />
                        <BooleanControl
                            label="Disabled"
                            checked={isDisabled}
                            onChange={setIsDisabled}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={
                    anatomy.isActive ? (
                        "x-ray · OTPField"
                    ) : (
                        <>
                            OTPField · {fillState} · {VERIFICATION_CODE_LENGTH}{" "}
                            digits
                        </>
                    )
                }
                belowStage={<OTPFieldMatrix />}
                output={JSON.stringify(
                    {
                        OTPField: {
                            value,
                            "aria-invalid": isInvalid,
                            disabled: isDisabled,
                            "data-forced-state": forcedState ?? null,
                        },
                        showcaseState: {
                            fillState,
                            codeLength: VERIFICATION_CODE_LENGTH,
                        },
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <OTPField
                        value={value}
                        onValueChange={setValue}
                        aria-label="Verification code"
                        aria-invalid={isInvalid}
                        disabled={isDisabled}
                        data-forced-state={forcedState}
                    />
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
